import { useState } from "react";
import { Play, Pause, Square, Volume2, ChevronDown, Zap } from "lucide-react";
import { saveSpeechPrefs, getSpeechPrefs, azureAvailable, AZURE_VOICES } from "../hooks/useSpeech";

const SPEEDS = [0.5, 1, 1.5, 2];

export default function SpeechPlayer({
  supported, browserSupported, speaking, paused, progress, voices,
  rate, voiceName, engineMode,
  onPlay, onPause, onStop, onRateChange, onVoiceChange, onEngineModeChange,
}) {
  const [showVoices, setShowVoices] = useState(false);

  if (!supported) {
    return (
      <div className="mt-4 rounded-xl px-4 py-3 text-xs text-zinc-400 glass border border-white/[0.06]">
        Text-to-speech is not supported in your browser. Please use Chrome or Edge for the best experience.
      </div>
    );
  }

  const isAzure = engineMode === "azure" && azureAvailable;
  const englishVoices = voices.filter(v => v.lang?.startsWith("en"));

  // Display label for current voice
  let displayVoice = "Default Voice";
  if (isAzure) {
    const av = AZURE_VOICES.find(v => v.name === voiceName);
    displayVoice = av ? av.label.split("—")[0].trim() : (AZURE_VOICES[0]?.label.split("—")[0].trim() || "Jenny");
  } else {
    const bv = englishVoices.find(v => v.name === voiceName);
    displayVoice = bv ? bv.name.split("(")[0].trim() : (englishVoices[0]?.name.split("(")[0].trim() || "Default");
  }

  const handleVoice = (name) => {
    onVoiceChange(name);
    saveSpeechPrefs({ voiceName: name });
    setShowVoices(false);
  };

  const handleRate = (r) => {
    onRateChange(r);
    saveSpeechPrefs({ rate: r });
  };

  const handleEngineMode = (mode) => {
    onEngineModeChange?.(mode);
    saveSpeechPrefs({ engineMode: mode });
  };

  return (
    <div className="mt-4 glass rounded-xl p-3 border border-white/[0.08] animate-fade-up">
      {/* Engine toggle (only if Azure key is set) */}
      {azureAvailable && (
        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-white/[0.06]">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">Voice Quality</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleEngineMode("browser")}
              className={`text-[10px] px-2 py-1 rounded transition-colors ${!isAzure ? "bg-indigo-600 text-white" : "glass text-zinc-400 hover:text-zinc-200"}`}
            >
              Standard — Free
            </button>
            <button
              onClick={() => handleEngineMode("azure")}
              className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded transition-colors ${isAzure ? "bg-indigo-600 text-white" : "glass text-zinc-400 hover:text-zinc-200"}`}
            >
              <Zap size={9}/> Enhanced HD
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <Volume2 size={13} className="text-indigo-300 flex-shrink-0"/>

        {/* Play / Pause */}
        <button
          onClick={speaking && !paused ? onPause : onPlay}
          title={speaking && !paused ? "Pause" : "Play"}
          className="w-8 h-8 rounded-lg bg-indigo-600 hover:bg-indigo-700 flex items-center justify-center text-white flex-shrink-0 transition-colors"
        >
          {speaking && !paused ? <Pause size={13}/> : <Play size={13}/>}
        </button>

        {/* Stop */}
        <button
          onClick={onStop}
          disabled={!speaking && !paused}
          title="Stop"
          className="w-8 h-8 rounded-lg glass hover:bg-white/10 flex items-center justify-center text-zinc-300 disabled:opacity-30 transition-colors flex-shrink-0"
        >
          <Square size={13}/>
        </button>

        {/* Progress bar */}
        <div className="flex-1 min-w-12 h-1.5 bg-white/[0.08] rounded-full overflow-hidden">
          <div
            className="h-full bg-indigo-500 rounded-full transition-all duration-200"
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>

        {/* Speed */}
        <div className="flex items-center gap-1">
          {SPEEDS.map(s => (
            <button
              key={s}
              onClick={() => handleRate(s)}
              className={`text-[10px] px-1.5 py-1 rounded font-mono transition-colors ${rate === s ? "bg-indigo-600 text-white" : "glass text-zinc-400 hover:text-zinc-200"}`}
            >
              {s}x
            </button>
          ))}
        </div>

        {/* Voice picker */}
        <div className="relative">
          <button
            onClick={() => setShowVoices(p => !p)}
            className="flex items-center gap-1 glass px-2.5 py-1.5 rounded-lg text-[11px] text-zinc-300 hover:text-white transition-colors max-w-[130px]"
          >
            <span className="truncate">{displayVoice}</span>
            <ChevronDown size={11} className="flex-shrink-0 opacity-60"/>
          </button>
          {showVoices && (
            <div className="absolute right-0 bottom-full mb-1.5 w-60 max-h-52 overflow-y-auto glass-strong rounded-xl border border-white/[0.1] z-50 shadow-2xl scroll-slim">
              {isAzure ? (
                AZURE_VOICES.map(v => (
                  <button key={v.name} onClick={() => handleVoice(v.name)}
                    className={`w-full text-left text-xs px-3 py-2.5 hover:bg-white/[0.08] transition-colors ${voiceName === v.name ? "text-indigo-300" : "text-zinc-300"}`}>
                    {v.label}
                  </button>
                ))
              ) : (
                <>
                  {englishVoices.length === 0 && (
                    <div className="px-3 py-3 text-xs text-zinc-500 text-center">Loading voices…<br/>Try again in a moment.</div>
                  )}
                  {englishVoices.map(v => (
                    <button key={v.name} onClick={() => handleVoice(v.name)}
                      className={`w-full text-left text-xs px-3 py-2 hover:bg-white/[0.08] transition-colors truncate ${voiceName === v.name ? "text-indigo-300" : "text-zinc-300"}`}>
                      {v.name}
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
